import type { CostRate } from 'api/cost-rates';
import type { PartnerPermission } from 'api/partner/types';
import type {
  FieldAccessMeta,
  FieldAccessMetaInBase3,
  FieldValue,
  FieldValueType,
} from 'api/tasks';
import type { TimeoffResponse } from 'api/timeoff/types';
import { CustomOption } from 'components/atoms/select';
import type { FormResponse } from 'components/organisms/forms/form-types';
import type { ColumnOrder } from 'components/organisms/grid-custom-column';
import { Minutes } from 'components/organisms/user-capacity/types';
import { TemplateId, TemplateObjectType } from 'data/templates/api';
import type { DocumentTemplate } from 'data/templates/documents';
import type { ProjectTemplateApi } from 'data/templates/projects';

export type UserLinkedResource = {
  integrationId: number;
  externalResourceType: string;
  externalResourceId: string;
  externalIntegrationType: string;
  enabled: boolean;
  deleted: boolean;
};

// TODO:: Use MemberType in places instead of UserType. Later extend from this if additional
export enum MemberType {
  Native = 'NATIVE',
  External = 'EXTERNAL',
  ExternalPartner = 'EXTERNAL_PARTNER',
  Partner = 'PARTNER',
}

export interface Team {
  teamId: number;
  teamName: string;
  teamType: MemberType;
  teamDescription?: string;
  roleId?: number;
  projectId?: number;
  companyList?: Pick<Company, 'companyId'>[];
  userList?: User[];
  projectIds?: number[];
  accountLevelTeam?: boolean;
  resolved?: boolean;
  isAdditionalHours?: boolean;
  budgetIds?: number[];
}
export interface TeamMeta {
  totalTask: number;
  privateTask: number;
  publicTask: number;
}
export interface TeamWithMeta extends Team {
  meta: TeamMeta;
}
export enum RoleType {
  Vendor = 'VENDOR',
  Customer = 'CUSTOMER',
}

export enum RateCardSourceType {
  USER = 'USER',
  ROLE = 'ROLE',
  TEAM = 'TEAM',
}

export interface Role {
  roleId: number;
  roleName: string;
  roleDescription?: string;
  usedByPartners?: boolean;
  type: RoleType;
}
export enum LoginPolicyType {
  RocketlaneAuth = 'ROCKETLANE_AUTH',
  MagicLink = 'MAGIC_LINK',
  Idp = 'IDP',
}
export interface BaseAuthenticationType<T> {
  companyId: number;
  authType: T;
  mandatory: boolean;
  enabled: boolean;
  default: boolean;
}
interface IdpAuthenticationType
  extends BaseAuthenticationType<LoginPolicyType.Idp> {
  idpConfigId: number;
}

export type AuthenticationType =
  | IdpAuthenticationType
  | BaseAuthenticationType<LoginPolicyType.MagicLink>
  | BaseAuthenticationType<LoginPolicyType.RocketlaneAuth>;
export enum CompanyType {
  CustomerPartner = 'EXTERNAL_PARTNER',
  Customer = 'CUSTOMER',
  Partner = 'PARTNER',
  Vendor = 'VENDOR',
}
export interface Company {
  companyName: string;
  companyId: number;
  companyLogoUrl?: string;
  companyLogo?: string;
  companyUrl?: string;
  authentications?: AuthenticationType[];
  capacityMinutes: Minutes;
  companyType: CompanyType;
  accessLevel?: PartnerPermission; // this is only applicable to partner company
  strategic?: boolean;
  canTrackTime?: boolean; // this is only applicable to partner company
  createdAt?: number;
  maxSeat?: number;
  usedSeat?: number;
  preferredTaxcodeId?: number;
}

export interface CompanyAddress {
  companyAddressId: number;
  companyId: number;
  addressName: string;
  country: string;
  state?: string;
  city?: string;
  zip?: string;
  email: string;
  phoneNumber?: string;
  addressLinePrimary?: string;
  addressLineSecondary?: string;
  contactName?: string;
  default: boolean;
}

export enum UserType {
  Native = 'NATIVE',
  External = 'EXTERNAL',
  Partner = 'PARTNER',
  CustomerPartner = 'EXTERNAL_PARTNER',
  CustomerPortalBot = 'CUSTOMER_PORTAL_BOT',
}

export type OutOfOfficeConfig = {
  message?: string;
  teamMembers?: number[];
  fields?: number[];
  placeholders?: number[];
  isEnabled?: boolean;
};

export type OutOfOfficeMeta = {
  isOutOfOfficeTemplateMessage?: boolean;
  isOutOfOfficeNotification?: boolean;
};

/**
 * @deprecated
 *
 * Use `ProjectPermissionSetType` and `AccountPermissionSetType` instead
 */
export enum PermissionSetType {
  AccountAdmin = 1,
  ProjectOwner,
  Collaborator,
  CustomerCollaborator,
  CustomerChampion,
  Observer,
  SuperUser,
  AccountVendorCollaborator,
  AccountCustomerCollaborator = 10,
  PartnerAdmin = 11,
  PartnerMember = 12,
  PartnerCollaborator = 13,
  IcertisPmAccountRole = 100,
  CustomerPartner = 20,
  CustomerPartnerCollaborator = 22,
}

export enum UserStatus {
  Active = 'ACTIVE',
  Inactive = 'INACTIVE',
  Invited = 'INVITED',
  Passive = 'PASSIVE',
}

export enum IntegrationStatus {
  Connected = 'CONNECTED',
  NotConnected = 'NOT_CONNECTED',
}

export interface UserGroup {
  userGroupsId: number;
  userGroupName: string;
}

export interface User {
  userTimeOffDetails?: TimeoffResponse[];
  createdAt?: number;
  firstName?: string;
  lastName?: string;
  emailId: string;
  userId: number;
  photoUrl?: string;
  userType: UserType;
  company: Company;
  role?: Role;
  userStatus: UserStatus;
  timeZone?: string;
  fields?: Array<FieldValue<FieldValueType>>;
  defaultCapacity: boolean;
  capacityMinutes: Minutes; // weekly capacity
  projectIds?: Array<number>;
  timeSheetApprover?: number;
  meetingRecordingEnabled?: boolean;
  emailIntelligenceEnabled?: boolean;
  isEmailAuthNeeded?: boolean;
  calendarStatus?: boolean;
  integration?: {
    email?: {
      emailStatus?: IntegrationStatus;
    };
  };
  holidayCalendarId?: number;
  costRate?: CostRate;
  reportingTo?: number;
  reportingToUser?: User;
  managerOf?: number[];
  managerOfUsers?: User[];
  userGroups: UserGroup[];
  permissionSet?: {
    permissionSetId: number;
    permissionSetName: string;
    permissionSetType: number;
  };
  fieldsAccessMeta?: FieldAccessMeta;
  fieldsAccessMetaInBase3?: FieldAccessMetaInBase3;
  emailLanguagePreference?: CustomOption | string;
  linkedResources?: UserLinkedResource[];
}

export interface Session {
  sessionPayload: {
    user: User;
    permissionSet: PermissionSet;
  };
}

export interface SignupSession {
  sessionToken: string;
}

/**
 * @deprecated
 * Use PermissionSet from `stores/permission-sets` instead
 */
export interface PermissionSet {
  permissionSetId: number;
  permissionSetName: string;
  permissionSetType: PermissionSetType;
}
export interface ProjectMember extends User {
  projects?: {
    [key: number]: {
      permissionSet: PermissionSet;
      invitationStatus: {
        active?: boolean;
        joinedAt?: number;
      };
    };
  };
}
export interface Squad {
  users: User[];
  teams: Team[];
}

// TODO:: Move this to proper type instead of number or pull it from models/tokens.
export type Timestamp = number;
export type DateType = string; // YYYY-MM-DD

export const DEFAULT_DATE_FORMAT = 'YYYY-MM-DD';

export const DISPLAY_FORMAT = 'DD MMM YY, hh:mm A';
export const DISPLAY_FORMAT_WITHOUT_TIME = 'DD MMM YY';
export const DATE_INPUT_DISPLAY_FORMAT = 'DD MMM YYYY';

// https://stackoverflow.com/a/57572747/5277189
type AtLeastOne<T, U = { [K in keyof T]: Pick<T, K> }> = Partial<T> &
  U[keyof U];

export type BulkActionPayload<T> = AtLeastOne<{
  create: T[];
  update: T[];
  delete: T[];
}>;

export enum OrderType {
  AllTasks = 'all_tasks',
  ProjectList = 'project_list',
  ProjectTaskList = 'project_task_list',
  DrilldownList = 'drilldown_list',
}

export interface Order {
  key: string;
  viewId: number;
  columnOrder: ColumnOrder[];
  orderType: OrderType;
  createdAt?: number;
}

export interface FormResponseDraft {
  response: FormResponse;
  timestamp: number;
  formInstance: number;
}

export interface TemplateDraft {
  content: ProjectTemplateApi | DocumentTemplate;
  timestamp: number;
  templateId: TemplateId;
  templateType: TemplateObjectType;
}
